import { jsPDF } from 'jspdf';
import { Injectable, inject } from '@angular/core';
import { ClientConfig, I18NText } from '@core/api';
import { ClientConfigDBService } from '@core/db/client-config-db-service';
import { I18nTextPipe } from '@core/pipes/i18n-text.pipe';

@Injectable()
export class ProtocolPdfEmailService {
  private clientConfigDbService = inject(ClientConfigDBService);
  private i18nTextPipe = inject(I18nTextPipe);

  private clientConfig: ClientConfig | null | undefined;

  public constructor() {
    this.clientConfigDbService.getClientConfig().subscribe(
      (config) => {
        if (config) {
          this.clientConfig = config;
        }
      },
      () => {},
    );
  }

  public sendEmailWithAttachment(
    emailAddressToProtocolPdf: string,
    fileName: string,
    attachment: jsPDF,
  ) {
    const emlContent = this.createEmlContentWithAttachment(
      emailAddressToProtocolPdf,
      fileName,
      attachment,
    );
    const textFile = this.createEmlTextFile(emlContent);
    this.downloadEmlFile(textFile, fileName);
  }

  private createEmlContentWithAttachment(
    emailAddressToProtocolPdf: string,
    fileName: string,
    attachment: jsPDF,
  ) {
    const subjectText: I18NText = {
      germanText: this.clientConfig?.protocol.textEmailSubjectDe ?? '',
      frenchText: this.clientConfig?.protocol.textEmailSubjectFr ?? '',
    };
    const bodyText: I18NText = {
      germanText: this.clientConfig?.protocol.textEmailBodyDe ?? '',
      frenchText: this.clientConfig?.protocol.textEmailBodyFr ?? '',
    };

    let emlContent = 'To: <' + emailAddressToProtocolPdf + '>\r\n';
    if (this.clientConfig?.protocol.defaultEmailBccAddressToProtocolPdf) {
      emlContent += 'Bcc: <' + this.clientConfig.protocol.defaultEmailBccAddressToProtocolPdf + '>\r\n';
    }
    emlContent += 'Subject: ' + this.i18nTextPipe.transform(subjectText) + '\r\n';
    emlContent += 'Date: ' + new Date().toUTCString() + '\r\n';
    emlContent += 'X-Unsent: 1' + '\r\n';
    emlContent += 'Content-Type: multipart/mixed; boundary="boundary_text_string"' + '\r\n';
    emlContent += 'MIME-Version: 1.0' + '\r\n';

    emlContent += '\r\n' + '--boundary_text_string' + '\r\n';
    emlContent += 'Content-Type: text/html; charset=UTF-8' + '\r\n';
    emlContent += '\r\n<html><head><meta http-equiv="Content-Type" content="text/html; charset=utf-8">';
    emlContent += '\r\n</head><body>' + this.i18nTextPipe.transform(bodyText) + '</body></html>\r\n';

    emlContent += '\r\n' + '--boundary_text_string' + '\r\n';
    emlContent += 'Content-Type: application/pdf; name="' + fileName + '"' + '\r\n';
    emlContent += 'Content-Transfer-Encoding: base64' + '\r\n';
    emlContent += 'Content-Disposition: attachment; filename="' + fileName + '"' + '\r\n';
    emlContent += '\r\n' + this.splitBase64IntoLines(attachment.output('datauristring').split(',')[1]) + '\r\n';
    emlContent += '--boundary_text_string--' + '\r\n';
    return emlContent;
  }

  private splitBase64IntoLines(base64String: string): string {
    const lineLength = 76;
    const lines: string[] = [];
    for (let i = 0; i < base64String.length; i += lineLength) {
      lines.push(base64String.substring(i, i + lineLength));
    }
    return lines.join('\r\n');
  }

  private createEmlTextFile(emlContent: string) {
    const data = new Blob([emlContent], { type: 'message/rfc822' });
    return window.URL.createObjectURL(data);
  }

  private downloadEmlFile(textFile: any, fileName: string) {
    const downloadLink = document.createElement('a');
    const linkText = document.createTextNode('Download email file');
    downloadLink.appendChild(linkText);
    downloadLink.href = textFile;
    downloadLink.id = 'download-email-file';
    downloadLink.download = fileName.replace('.pdf', '') + '.eml';
    downloadLink.style.visibility = 'hidden';

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
}
